import {
  CasinoStartupConfigurations,
  OrbitStartupConfigurations
} from '../config-interfaces/casino-startup.configurations';
import {ApplicationStartupConfigurations} from '../config-interfaces/application-startup.configurations';
import {B2CStartupConfigurations} from '../config-interfaces/b2c-startup.configurations';
import {LauncherOverridesReader} from '../launcher-overrides-reader/launcher-overrides-reader';
import {LauncherOverrides} from '../config-interfaces/launcher-overrides';
import {IWindowService} from '../../services/window/window.service.interface';
import {IHttpService} from '../../services/http/http.service.interface';
import {ILoggerService} from '../../services/logger/logger.service.interface';
import {mockApplicationStartupConfigs} from '../../../utils/automation-helpers';
import {ALL_CONFIGS_KEY} from "../../../app-consts";
import {IConfigurationData} from "../../services/configurations/configuration-data.interface";
import {IAssetsMap} from "../../themes/assets/assets-map.interface";
import {IConfigInitializationParams} from "../../../settings/configs/config-initialization-params.interface";
import {IStorageService} from "../../services/storage/storage.service.interface";
import {getMiniOrbitName} from "../../services/mini-orbit/mini-orbit-consts";
import {createDefaultConfigs} from "../../../settings/configs/default-config";

const overrideJson = require('json-override');

export class ApplicationStartupConfigurationsReader {
  constructor(
              private _window: IWindowService,
              private _launcherOverridesReader: LauncherOverridesReader,
              private _http: IHttpService,
              private _logger: ILoggerService,
              private _storageService: IStorageService) {
  }

  private _launcherOverrides: LauncherOverrides | null;
  private _b2cConfigurations: B2CStartupConfigurations;
  private _skinFullName: string;

  async readApplicationConfigurations(): Promise<ApplicationStartupConfigurations> {
    this._launcherOverrides = this._launcherOverridesReader.getOverrides();
    this._b2cConfigurations = this._getB2CConfigurations();

    let casinoConfigurations = await this._getCasinoConfigurations();
    this._skinFullName = (`${casinoConfigurations.skin}_${casinoConfigurations.skinOverride}`).toLowerCase();


    const configs: ApplicationStartupConfigurations = {
      orbitServer: casinoConfigurations.sdk.config.ExtraProperties.paths.server,
      skin: casinoConfigurations.skin,
      skinOverride: casinoConfigurations.skinOverride,
      skinFullName: this._skinFullName,
      country: casinoConfigurations.country,
      orbit: casinoConfigurations.orbit,
      sdk: casinoConfigurations.sdk,
      admin: casinoConfigurations.adminConfig,
      b2c: this._b2cConfigurations,
      userSelectedState: casinoConfigurations.userSelectedState,
      useCountryAsState: casinoConfigurations.useCountryAsState,
      assetsMap: casinoConfigurations.assetsMap
    };

    return mockApplicationStartupConfigs(configs);
  }


  private async _getCasinoConfigurations(): Promise<CasinoStartupConfigurations> {
    let casinoConfigurations: CasinoStartupConfigurations = this._window.getGlobalProperty(ALL_CONFIGS_KEY);
    const skin = casinoConfigurations.skin.toLowerCase();
    const skinOverride = casinoConfigurations.skinOverride.toLowerCase();
    const lang = this._b2cConfigurations.language.iso3.toLowerCase();

    const {AssetsMap} = await import(/* webpackChunkName: "assets-map" */ '../../themes/assets/assets-map');

    const assetsMap = new AssetsMap({
      serverOrigin: casinoConfigurations.sdk.config.ExtraProperties.paths.server,
      skin: casinoConfigurations.skin,
      skinOverride: casinoConfigurations.skinOverride,
      language: this._b2cConfigurations.language.iso3,
      miniOrbit: getMiniOrbitName(this._storageService)
    })


    casinoConfigurations.orbit.config = await this._loadOrbitConfigFile(skin, skinOverride, assetsMap);
    casinoConfigurations.orbit.lang = await this._tryLoadOrbitLanguageOverrideFile(skin, skinOverride, lang);
    casinoConfigurations = await this._applyOrbitConfigsOverrides(casinoConfigurations, assetsMap);
    casinoConfigurations = this._applySdkConfigOverrides(casinoConfigurations);
    casinoConfigurations.assetsMap = assetsMap;
    return casinoConfigurations;

  }

  private async _loadOrbitConfigFile(skin: string, skinOverride: string, assetsMap: IAssetsMap): Promise<IConfigurationData> {
    try {
      const configModule = await import(`../../../settings/configs/${skin}/overrides/${skinOverride}/config`);

      const configInitParams: IConfigInitializationParams = {
        assetsMap: assetsMap
      }
      return configModule.default(configInitParams);
    } catch (err) {
      this._logger.error(`Failed to load orbit configuration file for skin ${skin}/${skinOverride}`, err);
      throw err;
    }

  }

  private async _tryLoadOrbitLanguageOverrideFile(skin: string, skinOverride: string, languageIso3: string): Promise<OrbitStartupConfigurations["lang"]> {
    try {
      const langOverrideModule = await import(`../../../settings/configs/${skin}/overrides/${skinOverride}/languages/${languageIso3}`);
      return langOverrideModule.default;
    } catch (err) {
      this._logger.debug(`There is no override file for language ${languageIso3} in skin ${skin}/${skinOverride}`, err);
    }

  }


  async _applyOrbitConfigsOverrides(casinoConfigurations: CasinoStartupConfigurations, assetsMap: IAssetsMap): Promise<CasinoStartupConfigurations> {
    casinoConfigurations.orbit.config = overrideJson(createDefaultConfigs({assetsMap: assetsMap}), casinoConfigurations.orbit.config);
    if(this._launcherOverrides && this._launcherOverrides.orbit && this._launcherOverrides.orbit.configs) {
      overrideJson(casinoConfigurations.orbit.config, this._launcherOverrides.orbit.configs);
    }
    return casinoConfigurations;
  }



  private _applySdkConfigOverrides(casinoConfigurations: CasinoStartupConfigurations): CasinoStartupConfigurations {

    if(!(casinoConfigurations.sdk && casinoConfigurations.sdk.config)) { // if SDK configs are not here it probably means the app is loaded from a fallback file
      return casinoConfigurations;
    }

    overrideJson(casinoConfigurations, {
      sdk: {
        config: (this._launcherOverrides && this._launcherOverrides.sdk) || {}
      }
    });

    overrideJson(casinoConfigurations, {
      sdk: {
        config: {
          Language: {
            iso3: this._b2cConfigurations.language.iso3,
            iso2: this._b2cConfigurations.language.iso2
          }
        }
      }
    });

    let orbitLang = casinoConfigurations.orbit.lang;
    if(orbitLang && orbitLang.text && orbitLang.text.SDKTexts) {
      overrideJson(casinoConfigurations.sdk.config.Language.text, orbitLang.text.SDKTexts);
    }

    return casinoConfigurations;
  }




  private _applyB2COverrides(b2cConfigs: B2CStartupConfigurations): B2CStartupConfigurations {

    if(this._launcherOverrides?.orbit) {
      let languageIso3Override = this._launcherOverrides.orbit.language;
      let languageIso2Override = this._launcherOverrides.orbit.iso2;

      if(languageIso3Override) {
        b2cConfigs.language = {...b2cConfigs.language, iso3: languageIso3Override};
      }

      if(languageIso2Override) {
        b2cConfigs.language = {...b2cConfigs.language, iso2: languageIso2Override};
      }

      let publicationIdOverride = this._launcherOverrides.orbit.publicationID;
      if(publicationIdOverride) {
        b2cConfigs.publicationId = publicationIdOverride;
      }
    }


    let segmentsOverrides = this._launcherOverrides?.webData?.segments;

    if(segmentsOverrides) {
      b2cConfigs.segments = {...b2cConfigs.segments, ...segmentsOverrides};
    }

    return b2cConfigs;
  }

  private _getB2CConfigurations() : B2CStartupConfigurations {
    let b2cConfigs = this._window.getGlobalProperty('webData') || {} as B2CStartupConfigurations;

    b2cConfigs.language = b2cConfigs.language || {};
    b2cConfigs.language.iso3 = b2cConfigs.language.iso3 || 'eng';
    b2cConfigs.language.iso2 = b2cConfigs.language.iso2 || 'en';


    if(this._launcherOverrides) {
      return this._applyB2COverrides(b2cConfigs);
    }

    return b2cConfigs;
  }



}












